import React, { FC, ReactElement, useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";

import { DeleteOutline } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";

import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Tooltip,
} from "@mui/material";

import { useAppDispatch, useAppSelector } from "../../hooks/reduxhook";

import {
  addUserFormatPrompt,
  addUserFormats,
  getType,
  getUserFormatPrompt,
  removeUserFormatPrompt,
  replaceUserFormatPrompt,
} from "../../redux/slices/typeSlice";

import {
  getloadingforuserformats,
  loadingforUserprompts,
} from "../../redux/slices/loadingSlice";

import { DELETE_USER_FORMAT, UPDATE_USER_FORMAT } from "../../endpoint";

import apiCall from "../../utils/apiCall";

import { addError, addSuccessMessage } from "../../redux/slices/errorSlice";

import ConfirmDeleteModal from "../ConfirmDelete";

interface IUserFormatProps {
  value: string;
}

const UserFormat: FC<IUserFormatProps> = ({ value }): ReactElement => {
  const dispatch = useAppDispatch();

  const { accounts } = useMsal();
  const account = accounts?.[0];

  const chatType = useAppSelector(getType);

  /*
   * This selector now returns string[].
   */
  const selectedUserFormatPrompts = useAppSelector(getUserFormatPrompt);

  const isSaving = useAppSelector(getloadingforuserformats);

  const [currentValue, setCurrentValue] = useState<string>(value);

  const [openEditModal, setOpenEditModal] = useState<boolean>(false);

  const [draftValue, setDraftValue] = useState<string>(value);

  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);

  const isSelected = selectedUserFormatPrompts.includes(currentValue);

  useEffect(() => {
    setCurrentValue(value);
    setDraftValue(value);
  }, [value]);

  /*
   * Toggle only the clicked prompt.
   *
   * The addUserFormatPrompt reducer:
   * - adds the prompt if it is not selected
   * - removes the prompt if it is already selected
   */
  const togglePromptSelection = (prompt: string): void => {
    dispatch(addUserFormatPrompt(prompt));
  };

  const handleCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    event.stopPropagation();
    togglePromptSelection(currentValue);
  };

  const handleCheckboxClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    /*
     * Prevent the ListItemButton onClick from also firing.
     * Without this, the Redux toggle could run twice.
     */
    event.stopPropagation();
  };

  const handleRowClick = (): void => {
    togglePromptSelection(currentValue);
  };

  const deleteUserFormat = (promptToDelete: string): void => {
    if (!account?.username) {
      dispatch(addError("Unable to identify the logged-in user."));
      return;
    }

    dispatch(loadingforUserprompts(true));

    apiCall("POST", DELETE_USER_FORMAT, {
      prompt: promptToDelete,
      user_email: account.username,
      chat_type: chatType.prompt_storage,
    })
      .then((response: any) => {
        dispatch(addUserFormats(response.result));

        /*
         * Remove the deleted prompt from the selected
         * prompt array without affecting other selections.
         */
        dispatch(removeUserFormatPrompt(promptToDelete));

        setShowConfirmDelete(false);

        dispatch(addSuccessMessage("Prompt deleted successfully"));
      })
      .catch(() => {
        dispatch(addError("Failed to delete the prompt. Please try again."));
      })
      .finally(() => {
        dispatch(loadingforUserprompts(false));
      });
  };

  const updateUserFormat = (oldPrompt: string, newPrompt: string): void => {
    if (!account?.username) {
      dispatch(addError("Unable to identify the logged-in user."));
      return;
    }

    dispatch(loadingforUserprompts(true));

    apiCall("POST", UPDATE_USER_FORMAT, {
      old_prompt: oldPrompt,
      new_prompt: newPrompt,
      user_email: account.username,
      chat_type: chatType.prompt_storage,
    })
      .then((response: any) => {
        /*
         * Replace the old selected prompt with the new prompt.
         * If the old prompt was not selected, the reducer
         * leaves the selected array unchanged.
         */
        dispatch(
          replaceUserFormatPrompt({
            oldPrompt,
            newPrompt,
          }),
        );

        dispatch(addUserFormats(response.result));

        setCurrentValue(newPrompt);
        setDraftValue(newPrompt);
        setOpenEditModal(false);

        dispatch(addSuccessMessage("Prompt updated successfully"));
      })
      .catch(() => {
        dispatch(
          addError("Failed to update the prompt value. Please try again."),
        );
      })
      .finally(() => {
        dispatch(loadingforUserprompts(false));
      });
  };

  const handleOpenEdit = (event: React.MouseEvent<SVGSVGElement>): void => {
    event.stopPropagation();

    if (isSaving) {
      return;
    }

    setDraftValue(currentValue);
    setOpenEditModal(true);
  };

  const handleCancelEdit = (): void => {
    if (isSaving) {
      return;
    }

    setDraftValue(currentValue);
    setOpenEditModal(false);
  };

  const handleSaveEdit = (): void => {
    if (isSaving) {
      return;
    }

    const trimmedPrompt = draftValue.trim();

    if (!trimmedPrompt) {
      return;
    }

    if (trimmedPrompt === currentValue) {
      setOpenEditModal(false);
      return;
    }

    updateUserFormat(currentValue, trimmedPrompt);
  };

  const handleDeleteClick = (event: React.MouseEvent<SVGSVGElement>): void => {
    event.stopPropagation();
    setShowConfirmDelete(true);
  };

  return (
    <ListItem
      disablePadding
      sx={{
        display: "block",
      }}
    >
      <ListItemButton
        onClick={handleRowClick}
        selected={isSelected}
        sx={{
          minHeight: 48,
          justifyContent: "initial",
          alignItems: "flex-start",

          "&.Mui-selected": {
            backgroundColor: "rgba(46, 125, 50, 0.08)",
          },

          "&.Mui-selected:hover": {
            backgroundColor: "rgba(46, 125, 50, 0.12)",
          },
        }}
      >
        <Checkbox
          id={`promptcheckbox-${currentValue}`}
          color="success"
          size="small"
          checked={isSelected}
          onChange={handleCheckboxChange}
          onClick={handleCheckboxClick}
          sx={{
            m: 0,
            p: 0,
            mr: 0.5,
            mt: 0.4,
          }}
          inputProps={{
            "aria-label": `Select prompt ${currentValue}`,
          }}
        />

        <ListItemText
          id={`promptvalue-${currentValue}`}
          primary={currentValue}
          sx={{
            opacity: 1,
            p: 0,
            m: 0,
            px: 0.3,
            wordBreak: "break-word",
            whiteSpace: "normal",
          }}
          primaryTypographyProps={{
            sx: {
              whiteSpace: "normal",
              wordBreak: "break-word",
            },
          }}
        />

        <Tooltip title="Edit">
          <EditIcon
            id={`editicon-${currentValue}`}
            color="primary"
            fontSize="small"
            onClick={handleOpenEdit}
            sx={{
              mt: 0.4,
              cursor: isSaving ? "not-allowed" : "pointer",
              flexShrink: 0,
              opacity: isSaving ? 0.5 : 1,
            }}
            aria-label={`Edit prompt ${currentValue}`}
          />
        </Tooltip>

        <Tooltip title="Delete">
          <DeleteOutline
            id={`deleteicon-${currentValue}`}
            color="primary"
            fontSize="small"
            onClick={handleDeleteClick}
            sx={{
              mt: 0.4,
              ml: 0.5,
              cursor: "pointer",
              flexShrink: 0,
            }}
            aria-label={`Delete prompt ${currentValue}`}
          />
        </Tooltip>
      </ListItemButton>

      <Divider />

      <ConfirmDeleteModal
        show={showConfirmDelete}
        cancel={() => {
          setShowConfirmDelete(false);
        }}
        confirm={() => {
          deleteUserFormat(currentValue);
        }}
      />

      <Dialog
        open={openEditModal}
        onClose={handleCancelEdit}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            fontSize: "large",
          }}
        >
          Edit Prompt
        </DialogTitle>

        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            value={draftValue}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setDraftValue(event.target.value);
            }}
            size="small"
            multiline
            minRows={2}
            maxRows={6}
            placeholder="Update your format..."
            disabled={isSaving}
            onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
              if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
                event.preventDefault();
                handleSaveEdit();
              }
            }}
            sx={{
              mt: 1,

              "& .MuiOutlinedInput-root": {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#fd5f07 !important",
                },

                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#fd5f07 !important",
                },

                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#fd5f07 !important",
                  borderWidth: "1px",
                },

                "&.Mui-focused": {
                  boxShadow: "none",
                },
              },
            }}
          />
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "center",
            gap: 2,
            pb: 2,
          }}
        >
          <Button
            onClick={handleCancelEdit}
            variant="outlined"
            disabled={isSaving}
          >
            Cancel
          </Button>

          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={isSaving || !draftValue.trim()}
            sx={{
              minWidth: 130,
            }}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </ListItem>
  );
};

export default UserFormat;
